import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../software/AuthContext";
import { api } from "../../software/api";
import { toast } from "sonner";
import Report from "./Report";
import Logo from "../Logo";
import { LogOut, Droplets, ShoppingBag, FileText, ScrollText, Wrench, CalendarClock, ShoppingCart, Plus, Minus, Search, Send, ClipboardList, Home, ExternalLink, CheckCircle2 } from "lucide-react";

const dot = { ok: "#059669", low: "#D97706", high: "#DC2626" };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const QUOTE_STYLE = { draft: "bg-slate-100 text-slate-600", sent: "bg-sky-100 text-sky-700", accepted: "bg-emerald-100 text-emerald-700", converted: "bg-indigo-100 text-indigo-700", declined: "bg-rose-100 text-rose-700" };
const WO_STYLE = { booked: "bg-sky-100 text-sky-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700", invoiced: "bg-indigo-100 text-indigo-700", paid: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-500" };
const REORDER_STYLE = { new: "bg-amber-100 text-amber-700", processing: "bg-sky-100 text-sky-700", fulfilled: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-500" };

const SECTIONS = [
  { id: "pool", label: "My pool", icon: Home },
  { id: "history", label: "History", icon: ClipboardList },
  { id: "reorder", label: "Reorder", icon: ShoppingCart },
  { id: "service", label: "Request service", icon: Wrench },
];

function Card({ children, testid }) {
  return <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">{children}</div>;
}
function SectionTitle({ icon: I, children }) {
  return <h2 className="flex items-center gap-2 font-display font-bold text-slate-900 mb-3"><I className="h-4 w-4 text-sky-600" /> {children}</h2>;
}

function ReorderPanel({ myReorders, onReloaded }) {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // id -> {id,name,price,qty}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    const data = await api("GET", `/portal/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setProducts(data);
  }, [q]);
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (p) => setCart((c) => ({ ...c, [p.id || p.name]: { product_id: p.id || null, name: p.name, price: p.price, qty: (c[p.id || p.name]?.qty || 0) + 1 } }));
  const setQty = (k, d) => setCart((c) => { const q2 = (c[k].qty || 0) + d; if (q2 <= 0) { const { [k]: _, ...rest } = c; return rest; } return { ...c, [k]: { ...c[k], qty: q2 } }; });
  const lines = Object.entries(cart);
  const cartTotal = lines.reduce((s, [, v]) => s + v.price * v.qty, 0);

  const submit = async () => {
    if (lines.length === 0) { toast.error("Add at least one product."); return; }
    setBusy(true);
    try {
      await api("POST", "/portal/reorder", { items: lines.map(([, v]) => ({ product_id: v.product_id, name: v.name, qty: v.qty })), note });
      toast.success("Reorder request sent — we'll be in touch to confirm.");
      setCart({}); setNote(""); onReloaded();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="portal-reorder">
      <div>
        <SectionTitle icon={ShoppingCart}>Reorder chemicals & consumables</SectionTitle>
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="reorder-search" placeholder="Search products…" className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-sky-400" />
          </div>
          <button data-testid="reorder-search-btn" className="rounded-xl bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 transition-colors">Search</button>
        </form>
        <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
          {products.length === 0 && <p className="text-sm text-slate-400">No products found.</p>}
          {products.map((p) => (
            <div key={p.id} data-testid="reorder-product" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><ShoppingBag className="h-4 w-4 text-slate-400" /></div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                <p className="text-xs text-slate-400">{p.category || p.brand || p.sku}</p>
              </div>
              <span className="font-mono2 text-sm text-slate-600">{money(p.price)}</span>
              <button onClick={() => add(p)} data-testid="reorder-add-btn" className="rounded-full bg-sky-600 hover:bg-sky-700 text-white h-8 w-8 flex items-center justify-center shrink-0"><Plus className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Card testid="reorder-cart">
          <SectionTitle icon={ShoppingCart}>Your reorder</SectionTitle>
          {lines.length === 0 && <p className="text-sm text-slate-400">Add products from the left.</p>}
          <div className="space-y-2">
            {lines.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="flex-1 min-w-0 truncate text-slate-800">{v.name}</span>
                <button onClick={() => setQty(k, -1)} className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-6 text-center font-mono2">{v.qty}</span>
                <button onClick={() => setQty(k, 1)} className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /></button>
                <span className="w-16 text-right font-mono2 text-slate-600">{money(v.price * v.qty)}</span>
              </div>
            ))}
          </div>
          {lines.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between font-display font-bold text-slate-900">Est. total <span className="font-mono2">{money(cartTotal)}</span></div>}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="reorder-note" placeholder="Delivery or pickup notes (optional)…" className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
          <button onClick={submit} disabled={busy} data-testid="reorder-submit-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 transition-colors"><Send className="h-4 w-4" /> Send reorder request</button>
          <p className="text-[11px] text-slate-400 mt-2 text-center">Prices are estimates — we'll confirm the final invoice.</p>
        </Card>

        <Card>
          <SectionTitle icon={ClipboardList}>My requests</SectionTitle>
          {myReorders.length === 0 && <p className="text-sm text-slate-400">No reorder requests yet.</p>}
          <div className="space-y-2">
            {myReorders.map((r) => (
              <div key={r.id} data-testid="portal-my-reorder" className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">#{r.req_number} · {r.items.length} item(s)</p>
                  <p className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${REORDER_STYLE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function ServicePanel({ onSubmitted }) {
  const [f, setF] = useState({ subject: "", details: "", preferred_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject.trim()) { toast.error("Please describe the problem."); return; }
    setBusy(true);
    try {
      await api("POST", "/portal/service-request", { subject: f.subject.trim(), details: f.details, preferred_date: f.preferred_date || null });
      toast.success("Service request logged — we'll schedule it and get back to you.");
      setF({ subject: "", details: "", preferred_date: "" }); onSubmitted();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg" data-testid="portal-service">
      <SectionTitle icon={Wrench}>Request a service call</SectionTitle>
      <Card>
        <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400">What's the problem? *</label>
        <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="service-subject" placeholder="e.g. Pump is noisy / pool is green" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
        <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mt-3">More details</label>
        <textarea value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} rows={3} data-testid="service-details" placeholder="Anything that helps us prepare…" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
        <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mt-3">Preferred date (optional)</label>
        <input type="date" value={f.preferred_date} onChange={(e) => setF({ ...f, preferred_date: e.target.value })} data-testid="service-date" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
        <button onClick={submit} disabled={busy} data-testid="service-submit-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 transition-colors"><Send className="h-4 w-4" /> Send request</button>
      </Card>
    </div>
  );
}

export default function Portal() {
  const { user, logout } = useAuth();
  const [sec, setSec] = useState("pool");
  const [client, setClient] = useState(null);
  const [tests, setTests] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [myReorders, setMyReorders] = useState([]);
  const [branding, setBranding] = useState({});
  const [report, setReport] = useState(null);

  const loadCore = useCallback(async () => {
    const [cs, b] = await Promise.all([api("GET", "/clients"), api("GET", "/branding")]);
    const c = cs[0];
    setClient(c); setBranding(b);
    if (c) {
      const [t, p, q, w, s, r] = await Promise.all([
        api("GET", `/clients/${c.id}/tests`), api("GET", `/clients/${c.id}/purchases`),
        api("GET", "/portal/quotes"), api("GET", "/portal/workorders"),
        api("GET", "/portal/schedules"), api("GET", "/portal/reorders"),
      ]);
      setTests(t); setPurchases(p); setQuotes(q); setWorkorders(w); setSchedules(s); setMyReorders(r);
    }
  }, []);
  useEffect(() => { loadCore().catch((e) => console.error("Portal load failed:", e)); }, [loadCore]);

  const acceptUrl = (type, id) => `${window.location.origin}/accept/${type}/${id}`;

  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="client-portal">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="mx-auto max-w-5xl px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900 min-w-0">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-7 w-7 rounded object-contain" /> : <Logo showText={false} />}
            <span className="truncate">{branding.company || "Aqualux Nexus"}</span>
          </div>
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-6 sm:py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Hi {user.name}</h1>
        {client && <p className="text-slate-500 mt-1 capitalize text-sm">{client.pool?.type}{client.pool?.volume_litres ? ` · ${client.pool.volume_litres.toLocaleString()} L` : ""}{client.pool?.sanitiser ? ` · ${client.pool.sanitiser}` : ""}{client.pool?.mineral ? " · mineral" : ""}</p>}

        <div className="flex gap-2 mt-5 mb-6 overflow-x-auto pb-1">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSec(s.id)} data-testid={`portal-tab-${s.id}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${sec === s.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>

        {sec === "pool" && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="portal-pool">
            <Card>
              <SectionTitle icon={CalendarClock}>Upcoming service</SectionTitle>
              {schedules.length === 0 && workorders.filter((w) => ["booked", "in_progress"].includes(w.status)).length === 0 && <p className="text-sm text-slate-400">No visits booked.</p>}
              {schedules.map((s) => (
                <div key={s.id} data-testid="portal-schedule" className="flex justify-between items-center text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><p className="font-semibold text-slate-800">{s.title}</p><p className="text-[11px] text-slate-400 capitalize">{s.frequency}</p></div>
                  <span className="font-mono2 text-slate-600">{s.next_due}</span>
                </div>
              ))}
              {workorders.filter((w) => ["booked", "in_progress"].includes(w.status)).map((w) => (
                <div key={w.id} className="flex justify-between items-center text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><p className="font-semibold text-slate-800 capitalize">{w.type} · #{w.wo_number}</p><p className="text-[11px] text-slate-400">{w.description}</p></div>
                  <span className="font-mono2 text-slate-500">{w.scheduled_date || "TBC"}</span>
                </div>
              ))}
            </Card>
            <Card>
              <SectionTitle icon={Droplets}>Latest water test</SectionTitle>
              {tests.length === 0 && <p className="text-sm text-slate-400">No tests recorded yet.</p>}
              {tests[0] && (
                <div>
                  <div className="flex justify-between items-center"><span className="text-sm font-semibold text-slate-800">{new Date(tests[0].created_at).toLocaleDateString()}</span><button onClick={() => setReport(tests[0])} className="text-xs inline-flex items-center gap-1 text-sky-700 font-semibold"><FileText className="h-3.5 w-3.5" /> Report</button></div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(tests[0].summary || []).map((r) => <span key={r.key} className="inline-flex items-center gap-1 text-[10px] font-mono2 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200"><span className="h-2 w-2 rounded-full" style={{ background: dot[r.status] }} />{r.label} {r.value}</span>)}
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {sec === "history" && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="portal-history">
            <section>
              <SectionTitle icon={Droplets}>Water test history</SectionTitle>
              <div className="space-y-3">
                {tests.length === 0 && <p className="text-sm text-slate-400">No tests yet.</p>}
                {tests.map((t) => (
                  <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="portal-test">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-slate-800">{new Date(t.created_at).toLocaleDateString()}</span>
                      <button onClick={() => setReport(t)} className="text-xs inline-flex items-center gap-1 text-sky-700 font-semibold"><FileText className="h-3.5 w-3.5" /> Report</button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(t.summary || []).map((r) => <span key={r.key} className="inline-flex items-center gap-1 text-[10px] font-mono2 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200"><span className="h-2 w-2 rounded-full" style={{ background: dot[r.status] }} />{r.label} {r.value}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section className="space-y-6">
              <div>
                <SectionTitle icon={ScrollText}>Quotes</SectionTitle>
                <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {quotes.length === 0 && <p className="text-sm text-slate-400 p-4">No quotes.</p>}
                  {quotes.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-2 p-3.5 text-sm" data-testid="portal-quote">
                      <div className="min-w-0"><p className="font-semibold text-slate-800">#{q.quote_number} · {money(q.total)}</p><p className="text-[11px] text-slate-400">{(q.items || []).length} item(s)</p></div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${QUOTE_STYLE[q.status] || "bg-slate-100 text-slate-600"}`}>{q.status}</span>
                        {["sent", "draft"].includes(q.status) && <a href={acceptUrl("quote", q.id)} target="_blank" rel="noreferrer" data-testid="portal-quote-respond" className="text-[11px] font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1">Respond <ExternalLink className="h-3 w-3" /></a>}
                        {q.status === "accepted" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle icon={Wrench}>Service jobs & invoices</SectionTitle>
                <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {workorders.length === 0 && <p className="text-sm text-slate-400 p-4">No jobs yet.</p>}
                  {workorders.map((w) => (
                    <div key={w.id} className="flex items-center justify-between gap-2 p-3.5 text-sm" data-testid="portal-workorder">
                      <div className="min-w-0"><p className="font-semibold text-slate-800 capitalize">#{w.wo_number} · {w.type}</p><p className="text-[11px] text-slate-400 truncate">{w.description || w.scheduled_date}</p></div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono2 text-slate-600">{money(w.total)}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${WO_STYLE[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status.replace("_", " ")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle icon={ShoppingBag}>Products purchased</SectionTitle>
                <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {purchases.length === 0 && <p className="text-sm text-slate-400 p-4">No purchases recorded.</p>}
                  {purchases.map((p) => (
                    <div key={p.id} className="flex justify-between items-center p-3.5 text-sm" data-testid="portal-purchase">
                      <span className="text-slate-800">{p.product} <span className="text-slate-400">×{p.qty}</span></span>
                      <span className="font-mono2 text-slate-500">{new Date(p.date).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {sec === "reorder" && <ReorderPanel myReorders={myReorders} onReloaded={loadCore} />}
        {sec === "service" && <ServicePanel onSubmitted={loadCore} />}
      </main>
      {report && client && <Report branding={branding} client={client} test={report} onClose={() => setReport(null)} />}
    </div>
  );
}
